import { Add, Delete, WarningAmber } from '@mui/icons-material';
import { Box, Checkbox, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip } from '@mui/material';
import _ from 'lodash';
import React, { useContext, useState } from 'react';
import { DisableContext } from '../../../contexts/DisableContextProvider';
import { listPageTableRowStyling } from '../../../styles/sxStyling';
import { Employee } from '../../../types/Employee';
import { ConflictEmployeeEvent, EventEmployee } from '../../../types/Event';
import { UserConfigurableModule, UserSettingsCollapseKeys } from '../../../types/User';
import ResponsiveButton from '../../../uikit/buttons/ResponsiveButton';
import AccordionSection from '../../AccordionSection';
import AddEmployeeModal from './AddEmployeeModal';
import ConflictPopup, { ConflictState } from './ConflictPopover';
import DrawerLink from '../../common/drawers/ModuleDrawer/DrawerLink';

interface IEmployeeTableProps {
  eventEmployees: EventEmployee[];
  setEventEmployees: (eventEmployees: EventEmployee[]) => void;
}

export default function EmployeeTable({ eventEmployees, setEventEmployees }: IEmployeeTableProps) {
  const [selectedEmployees, setSelectedEmployees] = useState<string[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [conflictState, setConflictState] = useState<ConflictState>({
    anchorEl: null,
    conflicts: [],
  });

  const { disabled } = useContext(DisableContext);

  // Add employees
  const onAdd = (addedEmployees: Employee[]) => {
    const existing = _.map(eventEmployees, 'employee');
    const netNew = _.differenceBy(addedEmployees, existing, 'uuid');

    const newEventEmployees = [
      ...eventEmployees,
      ...netNew.map((emp) => ({
        employee: emp,
        conflict_employee_events: [],
      })),
    ];

    setEventEmployees(newEventEmployees);
  };

  // Remove employees
  const handleRemove = () => {
    const filtered = _.reject(eventEmployees, (ee) => _.includes(selectedEmployees, ee.employee.uuid));
    setEventEmployees(filtered);
    setSelectedEmployees([]);
  };

  const openConflict = (e: React.MouseEvent<HTMLElement>, conflicts: ConflictEmployeeEvent[]) => {
    e.stopPropagation();
    setConflictState({
      anchorEl: e.currentTarget,
      conflicts,
    });
  };

  const closeConflict = () => {
    setConflictState({
      anchorEl: null,
      conflicts: [],
    });
  };

  const employeeTableRows = _.map(eventEmployees, (ee) => {
    const employee = ee.employee;
    const conflicts = ee.conflict_employee_events || [];

    const handleSelectionChange = (checked: boolean) => {
      const nextSelections = checked ? [...selectedEmployees, employee.uuid] : _.reject(selectedEmployees, (id) => id === employee.uuid);
      setSelectedEmployees(nextSelections);
    };

    return (
      <TableRow key={employee.uuid} sx={{ ...listPageTableRowStyling }}>
        <TableCell>
          <Checkbox
            size="small"
            sx={{ p: 0 }}
            checked={_.includes(selectedEmployees, employee.uuid)}
            onChange={(event) => handleSelectionChange(event.target.checked)}
          />
        </TableCell>

        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {!!conflicts.length && (
              <Tooltip title="Conflicting events">
                <IconButton size="small" onClick={(e) => openConflict(e, conflicts)} sx={{ color: '#f39c12' }}>
                  <WarningAmber fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            <DrawerLink module={UserConfigurableModule.EMPLOYEES} uuid={employee.uuid}>
              {employee.display_name}
            </DrawerLink>
          </Box>
        </TableCell>

        <TableCell>{employee.roles_values?.[0]}</TableCell>
        <TableCell>{employee.email}</TableCell>
        <TableCell>{employee.phone}</TableCell>
      </TableRow>
    );
  });

  const actionButtons = (
    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'end' }}>
      {eventEmployees.length > 0 && (
        <ResponsiveButton startIcon={<Delete />} variant="outlined" color="warning" onClick={handleRemove} disabled={disabled}>
          Remove
        </ResponsiveButton>
      )}

      <ResponsiveButton startIcon={<Add />} variant="outlined" onClick={() => setModalOpen(true)} disabled={disabled}>
        Add
      </ResponsiveButton>
    </Box>
  );

  return (
    <AccordionSection
      title="Employees Assigned"
      headerElements={actionButtons}
      collapseId={UserSettingsCollapseKeys.EVENT_ITEM_EMPLOYEES}
      hasData={eventEmployees.length > 0}
    >
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Name</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Phone</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>{employeeTableRows}</TableBody>
        </Table>
      </TableContainer>

      <ConflictPopup conflictState={conflictState} onClose={closeConflict} />

      <AddEmployeeModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        alreadyAddedEmployees={_.map(eventEmployees, (ee) => ee.employee.uuid)}
        onAdd={onAdd}
      />
    </AccordionSection>
  );
}
